import { React, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "react-bootstrap";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleMenuDisplay,
  resetMenu,
  resetApp,
  resetUser,
  setActiveMenu,
  setActiveAccordion,
  setActiveSubMenu,
  clearCart,
} from "../../store/reducer";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { getMenuItems } from "../utilities";
import { signOut } from "../../store/reducer";
import { useTranslation } from "react-i18next";
import { renderTooltip } from "../helpful";
import { useQueryClient } from "@tanstack/react-query";
import { MenuSkeleton } from "../skeleton";
import { logout } from "../../api/api";

/**
 * A navigation menu component.
 * It uses the react-router-dom package to navigate to the first child route of the menu item clicked.
 * The menu items are fetched from the getMenuItems function.
 * The menu is displayed vertically if the window width is less than 768px, otherwise it is displayed horizontally.
 * The menu items are displayed as a list of links.
 * The menu items have a tooltip that displays the name of the menu item.
 * The menu items have a logout link that displays a confirm dialog when clicked.
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/05/12
 */
export default function Menu() {
  const toast = useRef(null);
  const menuItem = useRef([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const menu = useSelector((state) => state.menu);
  const app = useSelector((state) => state.user);
  const userID = app.userID;
  const activeMenuItem = menu.activeMenuItem;
  const [menuItems, setMenuItems] = useState([]);

  let userStats = [];
 
 
  for (let item in app.user.statUsers) {
   
    userStats.push(app.user.statUsers[item]);
  }
  

  useEffect(() => {
    const getItems = async () => {
      let items = await getMenuItems();

      setMenuItems(items);
    };
    getItems();
  }, []);

  const accept = async () => {
    let confirm = t("confirmed");
    let detail = t("you_have_accepted");
    let islogout = await logout();
    if (islogout) {
      queryClient.clear();
      dispatch(resetUser({}));
      dispatch(resetApp({}));
      dispatch(resetMenu({}));
      dispatch(clearCart());
      window.localStorage.clear();
      dispatch(signOut({}));

      navigate("/");
      toast.current.show({
        severity: "info",
        summary: confirm,
        detail: detail,
        life: 3000,
      });
    }
  };

  const reject = () => {
    return;
  };

  const handleMenuDisplay = (e, item, index) => {
    /**
     * Handles menu item clicks and redirects to the first child route if the item is a simple menu
     * or an accordion menu with children.
     * @param {React.SyntheticEvent} e - The event object
     * @param {{id: string, type: string, children: Array, name: string}} item - The menu item object
     * @param {number} index - The index of the item in the menu items array
     */

    e.preventDefault();

    let firstChildUrl;
    let firstChildID;

    if (item.type === "simple-menu") {
      if (item.hasOwnProperty("children") && item.children.length > 0) {
        firstChildUrl =
          item.id === "room_status"
            ? item.children[0].url + "/" + item.children[0].id
            : item.children[0].url;
        firstChildID = item.children[0].id;
      }
    } else if (item.type === "accordion-menu") {
      if (item.hasOwnProperty("children") && item.children.length > 0) {
        let temp = item.children[0];
        if (temp.hasOwnProperty("children") && temp.children.length > 0) {
          firstChildUrl =
            item?.name.toLowerCase() === "hotels"
              ? temp.children[0].url + "/" + item.children[0].id
              : temp.children[0].url;
          firstChildID = temp.children[0].id;
        }
      }
    }
    item.firstChildID = firstChildID;

    dispatch(toggleMenuDisplay(item));
    dispatch(setActiveMenu(item.id));
    dispatch(setActiveAccordion(0));
    dispatch(setActiveSubMenu(firstChildID));
    navigate(firstChildUrl);
  };

  const LogOut = (e) => {
    /**
     * Handles logout button clicks and shows a confirmation dialog to the user.
     * If the user confirms, it clears the cart and logs out the user.
     * @param {React.SyntheticEvent} e - The event object
     */
    e.preventDefault();
    let confirm = t("are_you_sure_to_log_out");
    let logout = t("log_out");

    confirmDialog({
      message: confirm,
      header: logout,
      icon: "pi pi-info-circle",
      defaultFocus: "accept",
      accept,
      reject,
    });
  };

  

  return (
    <>
      <div className={menu.display ? "navig-bar-block " : " h"}>
        <Toast ref={toast} />
        <ConfirmDialog />
        <Nav defaultActiveKey="/" className="navig-bar ">
          {menuItems.length > 0 ? (
            menuItems.map((item, index) => {
              
              if (item.name === "Statistics" && !userStats.includes(userID)) {
                return null;
              }else{

                return (
                  <OverlayTrigger
                    key={item.name}
                    placement={menu.display ? "top" : "right"}
                    delay={{ show: 50, hide: 50 }}
                    overlay={renderTooltip(item.name)}
                  >
                    <Nav.Link
                      key={item.id}
                      className={
                        "main-menu-item " +
                        (activeMenuItem === item.id ? " isActive " : " ")
                      }
                      id={item.id}
                      ref={(el) => (menuItem.current[index] = el)}
                      onClick={(e) => handleMenuDisplay(e, item, index)}
                    >
                      <span className={item.icon + " menu-icon"}></span>
                    </Nav.Link>
                  </OverlayTrigger>
                );
              }

            })
          ) : (
            <MenuSkeleton />
          )}
          <OverlayTrigger
            key={"logout"}
            placement={menu.display ? "top" : "right"}
            delay={{ show: 50, hide: 50 }}
            overlay={renderTooltip("Logout")}
          >
            <Nav.Link
              key={"Logout"}
              className="main-menu-item logout-icon mb-4"
              onClick={(e) => LogOut(e)}
            >
              <span className={"logout menu-icon"}></span>
            </Nav.Link>
          </OverlayTrigger>
        </Nav>
      </div>
    </>
  );
}
